import { client } from '@api/rest/client';
import type { ProvisionStatusResponseDTO } from '@conversed/rest/src/contracts/company/company.contract';
import type {
  TProvisioningDoneEvent,
  TProvisioningFailedEvent,
  TProvisioningStepEvent,
} from '@conversed/shared/src/central/constants/socket';
import type {
  TProvisioningStatus,
  TProvisioningStepStatus,
} from '@conversed/shared/src/central/types/provisioning.type';
import { toastErrorTranslated } from '@helpers/toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useProvisioningSocket } from './useProvisioningSocket';

export interface TProvisionStepView {
  status: TProvisioningStepStatus;
  error?: string | null;
}

// * Poll cadence while a run is in_progress. Socket events reach only the
// * initiating session (emitToUser), so a cross-session viewer or a reconnect
// * relies on this to keep the curtain moving.
const IN_PROGRESS_POLL_MS = 5000;

// * Teardown runs synchronously inside a single request and finishes in seconds,
// * so the provisioning cadence would make the whole unwind invisible.
const DEPROVISIONING_POLL_MS = 1000;

// * null means no polling - every other status is terminal or idle.
const resolvePollIntervalMs = (
  status: TProvisioningStatus | null,
): number | null => {
  if (status === 'in_progress') {
    return IN_PROGRESS_POLL_MS;
  }

  if (status === 'deprovisioning') {
    return DEPROVISIONING_POLL_MS;
  }

  return null;
};

const TEARDOWN_TERMINAL_STATUSES: TProvisioningStatus[] = [
  'deprovisioned',
  'deprovision_failed',
];

// * A poll can land before the backend has written 'deprovisioning' (it writes it
// * as its first act, but the first poll is only a second behind). Taking that
// * stale 'completed' would stop the poll dead - resolvePollIntervalMs returns
// * null for it - and the rest of the unwind would never be seen, so while a
// * teardown is in flight only its own terminal states are accepted.
const resolveFetchedStatus = (
  fetchedStatus: TProvisioningStatus | null,
  isTeardownInFlight: boolean,
): TProvisioningStatus | null => {
  if (!isTeardownInFlight) {
    return fetchedStatus;
  }

  if (fetchedStatus && TEARDOWN_TERMINAL_STATUSES.includes(fetchedStatus)) {
    return fetchedStatus;
  }

  return 'deprovisioning';
};

const buildStepStates = (
  steps: NonNullable<ProvisionStatusResponseDTO>['steps'],
): Map<number, TProvisionStepView> => {
  const stepStates = new Map<number, TProvisionStepView>();

  for (const step of steps) {
    stepStates.set(step.step, { status: step.status, error: step.error });
  }

  return stepStates;
};

export const useProvisioningStatus = (companyId: string) => {
  const [provision, setProvision] = useState<ProvisionStatusResponseDTO>(null);
  const [status, setStatus] = useState<TProvisioningStatus | null>(null);
  const [stepStates, setStepStates] = useState<Map<number, TProvisionStepView>>(
    new Map(),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const isTeardownInFlightRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await client.company.getProvisionStatus({
        params: { companyId },
      });
      const { data } = response;
      setProvision(data);
      setStatus(
        resolveFetchedStatus(
          data?.status ?? null,
          isTeardownInFlightRef.current,
        ),
      );
      setStepStates(data ? buildStepStates(data.steps) : new Map());
    } catch (fetchError) {
      console.error('[Provisioning] Failed to fetch status:', fetchError);
      toastErrorTranslated('provisioning.error.fetch-failed');
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  // * Ref so the socket callbacks and poll can refetch without re-subscribing.
  const fetchStatusRef = useRef(fetchStatus);

  useEffect(() => {
    fetchStatusRef.current = fetchStatus;
  }, [fetchStatus]);

  const handleStep = useCallback(
    (data: TProvisioningStepEvent) => {
      if (data.companyId !== companyId) {
        return;
      }

      setStatus('in_progress');
      setStepStates((previousStates) => {
        const nextStates = new Map(previousStates);
        nextStates.set(data.step, {
          status: data.status,
          error: data.status === 'failed' ? (data.detail ?? null) : null,
        });

        return nextStates;
      });
    },
    [companyId],
  );

  const handleDone = useCallback(
    (data: TProvisioningDoneEvent) => {
      if (data.companyId !== companyId) {
        return;
      }

      setStatus('completed');
      // * Reconcile with DB truth for the final step statuses.
      fetchStatusRef.current();
    },
    [companyId],
  );

  const handleFailed = useCallback(
    (data: TProvisioningFailedEvent) => {
      if (data.companyId !== companyId) {
        return;
      }

      setStatus('failed');
      // * Covers the synthetic proxy-mapping step, which never emits a step event.
      setStepStates((previousStates) => {
        const nextStates = new Map(previousStates);
        nextStates.set(data.step, { status: 'failed', error: data.error });

        return nextStates;
      });
      // * Reconcile the rollback states written after the failure.
      fetchStatusRef.current();
    },
    [companyId],
  );

  useProvisioningSocket(handleStep, handleDone, handleFailed);

  const retry = useCallback(async () => {
    setIsRetrying(true);

    try {
      await client.company.retryProvisioning({
        params: { companyId },
        body: undefined,
      });
      // * Optimistically flip to in_progress; the pipeline streams from here.
      setStatus('in_progress');
      await fetchStatusRef.current();
    } catch (retryError) {
      console.error('[Provisioning] Retry failed:', retryError);
      toastErrorTranslated('provisioning.error.retry-failed');
    } finally {
      setIsRetrying(false);
    }
  }, [companyId]);

  // * Optimistic flip for the caller that issues the teardown request: the
  // * request stays open for its whole duration, so the reverse curtain and its
  // * poll cadence would otherwise only kick in once it returns.
  const markDeprovisioning = useCallback(() => {
    isTeardownInFlightRef.current = true;
    setStatus('deprovisioning');
  }, []);

  // * Pairs with markDeprovisioning - the caller must call this once its request
  // * settles, either way. Dropping the suppression before the refetch is what
  // * keeps a teardown that never reached the backend from parking the curtain on
  // * 'deprovisioning' forever: the real status comes back instead.
  const settleDeprovisioning = useCallback(async () => {
    isTeardownInFlightRef.current = false;
    await fetchStatusRef.current();
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const pollIntervalMs = resolvePollIntervalMs(status);

    if (pollIntervalMs === null) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchStatusRef.current();
    }, pollIntervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [status]);

  return {
    provision,
    status,
    stepStates,
    isLoading,
    isRetrying,
    retry,
    markDeprovisioning,
    settleDeprovisioning,
  };
};
